import { utils } from "@intrasync/utils";
import type RootStore from "./RootStore";
import ServiceStore from "./ServiceStore";
import { ServiceStoreOptions } from "types/ServiceStoreTypes";
import { ServiceStoreFactory } from "./ServiceStoreFactory";

export interface ServiceStoreBatch {
  name: string;
  stores: Map<string, ServiceStore>;
  isInitialized: boolean;
}

/**
 * ServiceStoreManager - Manages batched service store initialization
 * Breaks down the 68+ service stores into manageable batches for lazy loading
 */
export class ServiceStoreManager {
  private rootStore: RootStore;
  private batches: Map<string, ServiceStoreBatch> = new Map();
  private batchSize: number;
  private serviceStoreConfigs: Array<{
    name: string;
    options: ServiceStoreOptions;
  }>;

  constructor(
    rootStore: RootStore,
    serviceStoreConfigs: Array<{ name: string; options: ServiceStoreOptions }>,
    batchSize = 15
  ) {
    this.rootStore = rootStore;
    this.serviceStoreConfigs = serviceStoreConfigs;
    this.batchSize = batchSize;
    this.createBatches();
  }

  private createBatches(): void {
    const totalServices = this.serviceStoreConfigs.length;
    // Silent batch creation
    const numBatches = Math.ceil(totalServices / this.batchSize);

    // Creating batches silently

    for (let i = 0; i < numBatches; i++) {
      const batchName = `batch-${i}`;
      const batch: ServiceStoreBatch = {
        name: batchName,
        stores: new Map(),
        isInitialized: false,
      };
      this.batches.set(batchName, batch);
    }
  }

  async initializeBatch(batchIndex: number): Promise<void> {
    const batchName = `batch-${batchIndex}`;
    const batch = this.batches.get(batchName);

    if (!batch || batch.isInitialized) return;

    const startIndex = batchIndex * this.batchSize;
    const endIndex = Math.min(
      startIndex + this.batchSize,
      this.serviceStoreConfigs.length
    );
    const batchServices = this.serviceStoreConfigs.slice(startIndex, endIndex);

    // Initializing batch with services

    for (const serviceConfig of batchServices) {
      try {
        // Use factory to create store with optional offline capabilities
        const store = ServiceStoreFactory.create(
          this.rootStore,
          serviceConfig.name,
          serviceConfig.options
        );
        batch.stores.set(serviceConfig.name, store);

        // Mount to RootStore using existing pattern
        const storeName = this.servicePathToStoreName(serviceConfig.name);
        // this.rootStore[storeName] = store;
        this.rootStore.mountStore(storeName, store, true);
      } catch (error) {
        console.error(
          `Failed to initialize store ${serviceConfig.name}:`,
          error
        );
      }
    }

    batch.isInitialized = true;
    // Batch initialized
  }

  async initializeEagerBatches(eagerBatchCount = 2): Promise<void> {
    const totalBatches = this.batches.size;
    const batchesToInitialize = Math.min(eagerBatchCount, totalBatches);

    // Initializing eager batches

    for (let i = 0; i < batchesToInitialize; i++) {
      await this.initializeBatch(i);
    }

    // Eager initialization complete
  }

  async ensureAllBatchesInitialized(): Promise<void> {
    const totalBatches = this.batches.size;
    const initializedCount = this.initializedBatches;
    const remainingBatches = totalBatches - initializedCount;

    if (remainingBatches > 0) {
      // Initializing remaining batches

      for (let i = 0; i < totalBatches; i++) {
        await this.initializeBatch(i); // initializeBatch already checks if initialized
      }

      // All batches initialized
    } else {
      // All batches already initialized
    }
  }

  async initializeLazyBatch(batchIndex: number): Promise<void> {
    if (batchIndex >= this.batches.size) {
      console.warn(`Batch index ${batchIndex} out of range`);
      return;
    }

    await this.initializeBatch(batchIndex);
  }

  async initializeAllBatches(): Promise<void> {
    // Initializing all batches...

    for (let i = 0; i < this.batches.size; i++) {
      await this.initializeBatch(i);
    }
  }

  getStore(serviceName: string): ServiceStore | undefined {
    for (const batch of this.batches.values()) {
      if (batch.isInitialized) {
        const store = batch.stores.get(serviceName);
        if (store) return store;
      }
    }
    return undefined;
  }

  disposeBatch(batchIndex: number): void {
    const batchName = `batch-${batchIndex}`;
    const batch = this.batches.get(batchName);

    if (!batch || !batch.isInitialized) return;

    // Disposing batch...

    for (const [serviceName, store] of batch.stores) {
      try {
        store.dispose();

        // Remove from RootStore
        const storeName = this.servicePathToStoreName(serviceName);
        this.rootStore.unmountStore(storeName);
      } catch (error) {
        console.error(`Error disposing store ${serviceName}:`, error);
      }
    }

    batch.stores.clear();
    batch.isInitialized = false;
  }

  disposeAll(): void {
    // Disposing all batches...

    for (let i = 0; i < this.batches.size; i++) {
      this.disposeBatch(i);
    }
  }

  private servicePathToStoreName(servicePath: string): string {
    // Use the same utility function as RootStore for consistency
    return `${utils.uriToCamel(servicePath)}Store`;
  }

  getBatchInfo() {
    return Array.from(this.batches.values()).map((batch, index) => ({
      index,
      name: batch.name,
      storeCount: batch.stores.size,
      initialized: batch.isInitialized,
      services: Array.from(batch.stores.keys()),
    }));
  }

  get totalBatches(): number {
    return this.batches.size;
  }

  get initializedBatches(): number {
    return Array.from(this.batches.values()).filter(
      (batch) => batch.isInitialized
    ).length;
  }

  get totalStores(): number {
    return Array.from(this.batches.values()).reduce(
      (sum, batch) => sum + batch.stores.size,
      0
    );
  }
}
